import { Inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { APP_TOP_LEVEL_PATH } from '@epicuro-next/navigation/constants';
import { selectAppRoutingReady } from '@epicuro-next/navigation/selectors';
import {
  EnvironmentSettings,
  ENVIRONMENT_SETTINGS,
} from '@epicuro-next/platform/environment';
import { go, redirectToUserUrl } from '@epicuro-next/state/router-store';
import {
  patchSession,
  selectSiteId,
  selectSubSiteId,
  SITE_ID,
  SUB_SITE_ID,
} from '@epicuro-next/state/session-state';
import { arrayIsEmpty } from '@epicuro-next/utilities';
import { getPulseUIRedirectTo } from '@epicuro-next/utilities-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Site } from 'graphql-api/gql-api-interfaces';
import { combineLatest, EMPTY, merge, of } from 'rxjs';
import {
  delay,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { setSelectedSite, updateCurrentSite } from '../actions/sites.actions';
import {
  getCurrentSiteUrl,
  getSiteIdFromParam,
  getSiteInfo,
  sitesArePresentInTheStartOfTheUrl,
  siteSliceUrlBuilder,
  skipMultiBrandSiteSelectorLogic,
} from '../models';
import { selectSites } from '../selectors/sites.selectors';

/**
 * Activated for multiple sites only
 */
@Injectable()
export class RouteEffects {
  // Set siteId from URL on user redirect fire
  private setRouterOnRedirectToUserUrlFrom$ = this.actions$.pipe(
    ofType(redirectToUserUrl),
    map(({ url }) => [url, getCurrentSiteUrl(getSiteIdFromParam(url))]),
  );

  // Set siteId from URL on initial loading
  public setRouterOnLoadSiteIdFromUrlState$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationStart),
    // take one should be before filter as it takes initial url
    take(1),
    filter(({ url }: NavigationStart) => {
      return (
        !getPulseUIRedirectTo() &&
        // ignore initial urlResolver url as it leads to race condition with go action dispatched from resolver flow
        !url.includes(APP_TOP_LEVEL_PATH.urlResolver.path)
      );
    }),
    map(({ url }: NavigationStart) => [
      url,
      getCurrentSiteUrl(getSiteIdFromParam(url)),
    ]),
  );

  // Restore URL strategy
  public restoreUrlStrategy$ = createEffect(() =>
    merge(
      this.setRouterOnRedirectToUserUrlFrom$,
      this.setRouterOnLoadSiteIdFromUrlState$,
    )
      .pipe(
        switchMap(
          ([url, siteIdArr]: [
            string,
            [string, string | undefined] | undefined,
          ]) =>
            this.store
              .pipe(
                select(selectSites),
                filter((arr) => !arrayIsEmpty(arr)),
                take(1),
                switchMap((sites) =>
                  this.store.pipe(
                    select(selectAppRoutingReady),
                    filter(Boolean),
                    take(1),
                    map(() => sites),
                  ),
                ),
              )
              .pipe(
                map(
                  (
                    sites: Site[],
                  ): [
                    string,
                    [string, string | undefined] | undefined,
                    Site[],
                    // replace old url bookmarks /#/login
                  ] => [url.replace(/#.*/, ''), siteIdArr, sites],
                ),
              ),
        ),
      )
      .pipe(
        delay(0),
        switchMap(
          ([url, siteIdArr, sites]: [
            string,
            [string, string | undefined] | undefined,
            Site[],
          ]) => {
            const siteId = (siteIdArr ?? [])[0];
            let subSiteId = (siteIdArr ?? [])[1];

            const [commands, searchParams] = url.split('?');
            const parsedParams = stringParamsToObject(searchParams);

            if (url.includes(APP_TOP_LEVEL_PATH.urlResolver.path)) {
              return of(
                go({
                  commands: [commands],
                  extras: { queryParams: parsedParams },
                }),
              );
            }

            if (skipMultiBrandSiteSelectorLogic(url) && sites.length !== 1) {
              return EMPTY;
            }

            // Set site as currSite if only one is returned
            if (sites.length === 1) {
              const actions = [
                setSelectedSite({ site: sites[0], skipLocationChange: true }),
                patchSession({
                  session: {
                    [SITE_ID]: sites[0].siteId,
                    [SUB_SITE_ID]: subSiteId,
                  },
                }),
              ];

              if (skipMultiBrandSiteSelectorLogic(url)) {
                return actions;
              }

              return [
                ...actions,
                go({
                  commands: [commands],
                  extras: { queryParams: parsedParams },
                }),
              ];
            }

            if (siteId) {
              const { site, subSite } = extractSiteAndSubSite(
                sites,
                siteId,
                subSiteId,
              );
              subSiteId = subSite?.subSiteId;

              if (site) {
                // if subSiteId is a part of URL as well then we have to find sub site of found site
                // otherwise redirect to selector, as user intended to get on subsite of concrete site
                if (subSiteId) {
                  if (subSite) {
                    return [
                      setSelectedSite({
                        site: getSiteInfo(site, subSite),
                        skipLocationChange: true,
                      }),
                      patchSession({
                        session: {
                          [SITE_ID]: site.siteId,
                          [SUB_SITE_ID]: subSite.subSiteId,
                        },
                      }),
                      go({
                        commands: [commands],
                        extras: { queryParams: parsedParams },
                      }),
                    ];
                  }

                  return of(
                    go({
                      commands: ['/', APP_TOP_LEVEL_PATH.siteSelector.path],
                    }),
                  );
                }

                return [
                  setSelectedSite({
                    site,
                    skipLocationChange: true,
                  }),
                  patchSession({ session: { [SITE_ID]: site.siteId } }),
                  go({
                    commands: [commands],
                    extras: { queryParams: parsedParams },
                  }),
                ];
              }

              return of(
                go({
                  commands: ['/', APP_TOP_LEVEL_PATH.siteSelector.path],
                }),
              );
            }

            return of(
              go({
                commands: ['/', APP_TOP_LEVEL_PATH.siteSelector.path],
              }),
            );
          },
        ),
      ),
  );

  // Listen to all router events and add siteId if not there
  public addSiteIdIfNotExist$ = createEffect(
    () =>
      combineLatest([
        this.router.events.pipe(
          filter((events) => events instanceof NavigationStart),
        ),
        this.store.pipe(select(selectSites)),
      ]).pipe(
        filter(
          ([, sites]) =>
            sites.length >
            (this.environmentSettings.multiSiteSupport === 'true' ? 0 : 1),
        ),
        map(([events, sites]) => ({ payload: events, sites })),
        withLatestFrom(
          this.store.pipe(select(selectSiteId)),
          this.store.pipe(select(selectSubSiteId)),
        ),
        tap(
          ([{ payload, sites }, siteId, subSiteId]: [
            { payload: NavigationStart; sites: Site[] },
            string | null,
            string | null,
          ]) => {
            const url = payload.url;
            let extras = this.router.getCurrentNavigation()?.extras;

            if (url.includes(APP_TOP_LEVEL_PATH.urlResolver.path)) {
              const [commands, searchParams] = url.split('?');
              const parsedParams = stringParamsToObject(searchParams);

              return of(
                go({
                  commands: [commands],
                  extras: { queryParams: parsedParams },
                }),
              );
            }

            if (skipMultiBrandSiteSelectorLogic(url, extras)) {
              return;
            }

            let newUrl = url;
            let selectedSite;
            let selectedSubSite;

            if (siteId) {
              if (
                APP_TOP_LEVEL_PATH.siteSelector.custom &&
                url.includes(`/${APP_TOP_LEVEL_PATH.siteSelector.custom}/`)
              ) {
                const [siteInUrl, ...restOfUrl] = url
                  .split(`/${APP_TOP_LEVEL_PATH.siteSelector.custom}/`)
                  ?.filter(Boolean)?.[0]
                  ?.split('/')
                  .filter(Boolean);

                [siteId, subSiteId] = siteInUrl.split(':');
                newUrl = restOfUrl.join('/');

                const { site, subSite } = extractSiteAndSubSite(
                  sites,
                  siteId,
                  subSiteId,
                );

                subSiteId = subSite?.subSiteId ?? null;

                this.store.dispatch(
                  patchSession({
                    session: {
                      siteId: site?.siteId,
                      subSiteId: subSite?.subSiteId,
                    },
                  }),
                );

                selectedSite = site;
                selectedSubSite = subSite;
              }

              if (selectedSite) {
                this.store.dispatch(
                  updateCurrentSite({
                    site: getSiteInfo(selectedSite, selectedSubSite),
                  }),
                );
              }

              if (sitesArePresentInTheStartOfTheUrl(url, siteId, subSiteId)) {
                return;
              }

              // to preserve absolute path
              if (url.startsWith('/')) {
                if (newUrl !== '/' && newUrl.startsWith('/')) {
                  newUrl = newUrl.slice(1);
                }

                const [urlPart, searchParams] = newUrl.split('?');
                if (extras?.queryParams || searchParams) {
                  const parsedParams = stringParamsToObject(searchParams);

                  extras = {
                    ...(extras ?? {}),
                    queryParams: {
                      ...parsedParams,
                      ...(extras?.queryParams ?? {}),
                    },
                  };
                }

                this.store.dispatch(
                  go({
                    commands: [
                      '/s',
                      siteSliceUrlBuilder(siteId, subSiteId),
                      urlPart,
                    ],
                    extras,
                  }),
                );
                return;
              }
            }

            this.store.dispatch(
              go({
                commands: ['/', APP_TOP_LEVEL_PATH.siteSelector.path],
                extras,
              }),
            );
          },
        ),
      ),
    { dispatch: false },
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    @Inject(ENVIRONMENT_SETTINGS)
    private environmentSettings: EnvironmentSettings,
  ) {}
}

function stringParamsToObject(searchParams: string) {
  return [...new URLSearchParams(searchParams ?? '').entries()].reduce(
    (acc: Record<string, unknown>, entry: string[]) => {
      acc[entry[0]] = entry[1];
      return acc;
    },
    {},
  );
}

export function extractSiteAndSubSite(
  sites: Site[],
  siteId: string,
  subSiteId?: string,
) {
  const site = sites.find((s) => s.siteId === siteId);
  let subSite =
    site?.subSites?.find((fsSubSite) => fsSubSite?.subSiteId === subSiteId) ??
    undefined;

  if (!subSite && site?.subSites?.length) {
    subSite =
      site.subSites.find((item) => item?.isBrandSelectorVisible) ?? undefined;
  }

  return {
    site,
    subSite,
  };
}
